/* eslint-disable camelcase */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ERChartData,
  ERRange,
  ERGraphDataPoint
} from './type';
import { getColors, getDisplayRange, getSeriesName, outOfRange } from './utils';
import { createERLineChartOptions } from './line-chart.options';
import VeryNiceChart from '../../../../../../styled-base/charts/VeryNiceChart';
import Empty from '../../../Empty';

export type ERLineChartProps = {
  id: number,
  okrs: ERChartData[];
  startDate: number;
  endDate: number;
  range: ERRange;
  isLoading: boolean;
  afterCreated?: () => void;
  classNameEmpty: string;
}

export default function ERLineChart ({
  id,
  okrs,
  startDate,
  endDate,
  range,
  isLoading,
  afterCreated,
  classNameEmpty
}: ERLineChartProps): React.ReactElement {
  const { t } = useTranslation();
  const translation = t;
  const [displayRange, isDifferentFromRange] = getDisplayRange(range, startDate, endDate);

  const getMinMax = (arr, key) => {
    const values = arr.map(value => value[key]);
    const result = {
      min: Math.min.apply(null, values),
      max: Math.max.apply(null, values)
    };
    return result;
  };

  // Convert incoming data to graph data series
  const dataSeries = React.useMemo(() => okrs
    .map((okr) => {
      // Handle different series formats
      let seriesData: ERGraphDataPoint[];
      if (Array.isArray(okr.series)) {
        // If series is directly an array of data points
        seriesData = okr.series;
      } else if (okr.series && typeof okr.series === 'object' && 'data' in okr.series) {
        // If series is an object with a data property
        seriesData = okr.series.data;
      } else {
        // Fallback to empty array
        seriesData = [];
      }

      return {
        ...okr,
        series: { data: seriesData },
      };
    })
    .map((okr, idx) => {
      const filteredData = okr.series.data.filter((fd) => outOfRange(fd.x, displayRange));
      const maxY = filteredData.length > 0
        ? filteredData.reduce((a, b) => (a.y > b.y ? a : b)).y
        : 0;

      return {
        ...okr,
        id: `er-linechart-okr-${okr.id}`,
        type: 'line' as any,
        color: getColors(idx),
        name: getSeriesName(idx, filteredData.length, translation),
        data: filteredData,
        minmax: getMinMax(filteredData, 'y')
      };
    }), [okrs, displayRange, translation]);

  const minmax = React.useMemo(() => ({
    min: dataSeries && dataSeries[0]
      ? Math.min(dataSeries[0].minmax.min, dataSeries[1].minmax.min)
      : null,
    max: dataSeries && dataSeries[0]
      ? Math.max(dataSeries[0].minmax.max, dataSeries[1].minmax.max)
      : null
  }), [dataSeries]);

  const options = React.useMemo(() => createERLineChartOptions(
    startDate,
    endDate,
    displayRange,
    minmax,
    translation
  ), [
    startDate,
    endDate,
    displayRange,
    minmax,
    translation
  ]);

  const key = React.useMemo(() => dataSeries.reduce((acc, { id: seriesId }) => `${acc}${seriesId}`, ''), [dataSeries]);
  const isEmpty = !isLoading && !dataSeries.length;

  return (
    <>
      {!isEmpty ? (
        <VeryNiceChart
          key={key}
          afterCreated={afterCreated}
          dataSeries={dataSeries}
          options={options}
          isPercentage={false}
          presentationFont={false}
          showChartLabels={false}
        />
      ) : (
        <Empty className={classNameEmpty} />
      )}
    </>
  );
}
